import React from "react";
import { Box, Card, Chip, Divider, Stack, Typography } from "@mui/material";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import BillingBadge from "./BillingBadge";
import type { StaffBlock } from "../types";

const to12h = (t: string | null) => {
  if (!t) return "—";
  const m = /^(\d{1,2}):(\d{2})/.exec(t);
  if (!m) return t;
  let h = Number(m[1]);
  const mm = m[2];
  const s = h >= 12 ? "PM" : "AM";
  h = h % 12; if (h === 0) h = 12;
  return `${h}:${mm} ${s}`;
};

export default function TimetableGrid({ staff }: { staff: StaffBlock }) {
  return (
    <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" mb={1}>
        <Typography variant="h6" fontWeight={700} color="#1b3a6b">
          {staff.staff_name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Week {staff.week_start} to {staff.week_end}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2}>
        <Chip size="small" label={`Classes: ${staff.summary.classes}`} />
        <Chip size="small" label={`Students: ${staff.summary.students}`} />
        <Chip size="small" label={`Hours: ${staff.summary.teaching_hours}`} />
        <Chip size="small" label={`Online: ${staff.summary.online}`} />
        <Chip size="small" label={`In-Person: ${staff.summary.in_person}`} />
        <Chip size="small" label={`Hybrid: ${staff.summary.hybrid}`} />
        {staff.summary.zero_student > 0 && (
          <Chip size="small" color="warning" label={`Zero-student: ${staff.summary.zero_student}`} />
        )}
        {staff.summary.locations.length > 0 && (
          <Chip size="small" variant="outlined" label={`Locations: ${staff.summary.locations.join(", ")}`} />
        )}
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {(() => {
        const daysWithClasses = staff.days.filter(
          (d) => Array.isArray(d.classes) && d.classes.length > 0
        );
        if (daysWithClasses.length === 0) {
          return (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
              No classes found for the selected week and filters.
            </Typography>
          );
        }
        return (
      <Stack spacing={2}>
        {daysWithClasses.map((d) => (
          <Box key={d.date}>
            <Typography fontWeight={700} color="#1b3a6b" mb={0.5}>
              {d.day} — {d.date}
            </Typography>
            {(
              <Stack spacing={1}>
                {d.classes.map((c) => (
                  <Card key={c.occurrence_id} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <CheckBoxOutlineBlankIcon fontSize="small" sx={{ color: "#94a3b8" }} />
                      <Typography fontWeight={700}>
                        {to12h(c.start_time)} – {to12h(c.end_time)}
                      </Typography>
                      <Chip size="small" variant="outlined" label={c.mode || "—"} />
                      <Chip size="small" variant="outlined" label={c.location || "Location: Not assigned"} />
                      <Typography variant="caption" color="text.secondary">
                        Class ID {c.class_id} · Occ {c.occurrence_id} · {c.enrolled_count}/{c.capacity ?? "—"}
                      </Typography>
                      {c.students.length === 0 && (
                        <Chip size="small" color="warning"
                          icon={<WarningAmberOutlinedIcon />}
                          label="No students" />
                      )}
                    </Stack>
                    {c.students.length > 0 && (
                      <Box mt={1} pl={4}>
                        {c.students.map((s) => (
                          <Stack
                            key={s.student_id}
                            direction={{ xs: "column", sm: "row" }}
                            spacing={{ xs: 0.5, sm: 1 }}
                            alignItems={{ xs: "flex-start", sm: "center" }}
                            sx={{ py: 0.25 }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                              <CheckBoxOutlineBlankIcon fontSize="small" sx={{ color: "#94a3b8" }} />
                              <Typography variant="body2">
                                {s.student_name}
                                {s.grade ? ` — GR ${s.grade}` : ""}
                                {s.subject ? ` — ${s.subject}` : ""}
                                {s.reference_id ? (
                                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                    ({s.reference_id})
                                  </Typography>
                                ) : null}
                              </Typography>
                            </Stack>
                            {s.billing ? (
                              <Box sx={{ pl: { xs: 3.5, sm: 0 } }}>
                                <BillingBadge billing={s.billing} />
                              </Box>
                            ) : null}
                          </Stack>
                        ))}
                      </Box>
                    )}
                  </Card>
                ))}
              </Stack>
            )}
          </Box>
        ))}
      </Stack>
        );
      })()}

      <Divider sx={{ my: 2 }} />
      <Box sx={{ border: "1px dashed #94a3b8", p: 1.5, borderRadius: 1 }}>
        <Typography variant="subtitle2" color="#1b3a6b" fontWeight={700} mb={1}>
          ADMIN REVIEW
        </Typography>
        <Typography variant="body2" component="div">
          ☐ All classes are correct &nbsp;&nbsp; ☐ Corrections are marked above &nbsp;&nbsp; ☐ Follow-up required
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>Reviewed by: ______________________</Typography>
        <Typography variant="body2">Date: _____________________________</Typography>
        <Typography variant="body2">Signature/Initials: _______________</Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>General Notes:</Typography>
      </Box>
    </Card>
  );
}
